import React, { ReactElement, useState } from 'react'
import translate from 'helpers/translate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  FA_CHECK,
  FA_EXCLAMATION_CIRCLE,
  FA_INFO_CIRCLE,
  FA_LOCK
} from 'const/iconProp'
import { NewPasswordType } from 'redux/registration/typings'
import determineValidationIcon from '../determineValidationIcon'
import { RulesContainer, TextfieldWithMargin } from '../subcomponents'
import RuleItem from '../RuleItem'

export interface CreatePasswordProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: () => void
  validationErrors: NewPasswordType
  updatePassword?: boolean
  name?: string
}

const CreatePassword = ({
  value,
  onChange,
  onBlur,
  validationErrors,
  updatePassword,
  onFocus,
  name,
  ...props
}: CreatePasswordProps): ReactElement => {
  const [showRulesContainer, setShowRulesContainer] = useState(false)

  const errorKey = Object.keys(validationErrors).find(
    (rule: keyof NewPasswordType) => !validationErrors[rule]
  )

  const pristine = Object.keys(validationErrors).find(
    (rule: keyof NewPasswordType) => validationErrors[rule] === 'pristine'
  )

  return (
    <>
      <TextfieldWithMargin
        id="password"
        data-testid="password"
        name={name || 'password'}
        type="password"
        value={value}
        onChange={onChange}
        maxLength={20}
        onFocus={() => {
          setShowRulesContainer(true)
          onFocus()
        }}
        onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
          onBlur(event)
          if (!event.target.value) {
            setShowRulesContainer(false)
          }
        }}
        placeholder={translate({ string: 'password' })}
        error={
          errorKey &&
          translate({
            string: errorKey === 'required' ? 'required' : 'newPassword'
          })
        }
        inputClassName="masked ensightencustomevent"
        data-reason="password"
        data-type="login"
        data-object="field"
        data-private
        leftIcon={<FontAwesomeIcon icon={FA_LOCK} />}
        errorIcon={<FontAwesomeIcon icon={FA_EXCLAMATION_CIRCLE} />}
        showMaskedOption
        hideTextValue={translate({ string: 'hide' })}
        showTextValue={translate({ string: 'show' })}
        width="275px"
        required
        {...props}
      />
      <RulesContainer
        showRulesContainer={showRulesContainer}
        data-testid="rulesContainer"
      >
        {showRulesContainer && (errorKey || pristine) ? (
          <>
            {Object.keys(validationErrors).map(rule => {
              const validationRule =
                validationErrors[rule as keyof NewPasswordType]
              const validationIcon = determineValidationIcon(validationRule)

              return (
                rule !== 'specialChar' &&
                rule !== 'required' &&
                rule !== 'lastFour' && (
                  <RuleItem
                    key={rule}
                    rule={rule}
                    icon={validationIcon}
                    pristine={validationRule === 'pristine'}
                    hasError={!validationRule}
                  />
                )
              )
            })}
            <RuleItem
              rule="specialChar"
              icon={<FontAwesomeIcon icon={FA_INFO_CIRCLE} />}
              pristine
            />

            {updatePassword && (
              <RuleItem
                rule="lastFour"
                icon={<FontAwesomeIcon icon={FA_INFO_CIRCLE} />}
                pristine
              />
            )}
          </>
        ) : (
          !errorKey &&
          !pristine && (
            <RuleItem
              rule="readyToGo"
              icon={<FontAwesomeIcon icon={FA_CHECK} />}
            />
          )
        )}
      </RulesContainer>
    </>
  )
}

export default CreatePassword
